import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Send,
  Loader2,
  BookOpen,
  FileText,
  AlertCircle,
  Bot,
  User,
  Trash2,
  ThumbsUp,
  ThumbsDown,
  MessageSquareWarning,
  FileQuestion,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface Citation {
  id: number;
  documentId?: string;
  filename: string;
  excerpt: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  timestamp: Date;
  error?: boolean;
  queryId?: string;
  feedbackGiven?: 'helpful' | 'not_helpful' | 'incorrect' | 'incomplete';
  originalQuestion?: string; // For "Explain More" tracking
  isExplanation?: boolean; // Flag for detailed explanations
  canExplainMore?: boolean; // Show "Explain More" button
}

interface ChatResponse {
  message: string;
  sessionId?: string;
  queryId?: string;
  mode: string;
  citations?: Citation[];
  responseTime?: number;
  modelId?: string;
  error?: boolean;
  isExplanation?: boolean;
  kbStatus?: {
    isReady: boolean;
    documentCount: number;
    status: string;
  };
}

type FeedbackRating = 'helpful' | 'not_helpful' | 'incorrect' | 'incomplete';

export function KBChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);


  const chatMutation = useMutation({
    mutationFn: async (message: string) => {
      const response = await apiClient.post<ChatResponse>('/chat', {
        message,
        sessionId,
        mode: 'knowledgebase',
      });
      return { data: response.data, originalQuestion: message };
    },
    onSuccess: ({ data, originalQuestion }) => {
      if (data.sessionId) {
        setSessionId(data.sessionId);
      }

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.message,
        citations: data.citations,
        timestamp: new Date(),
        error: data.error,
        queryId: data.queryId,
        originalQuestion,
        canExplainMore: !data.error && data.citations && data.citations.length > 0,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    },
    onError: (error: any) => {
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: error.response?.data?.message || 'An error occurred. Please try again.',
        timestamp: new Date(),
        error: true,
      };

      setMessages((prev) => [...prev, errorMessage]);
    },
  });

  const explainMoreMutation = useMutation({
    mutationFn: async ({ originalQuestion, shortAnswer }: { originalQuestion: string; shortAnswer: string }) => {
      const response = await apiClient.post<ChatResponse>('/chat', {
        explainMore: true,
        originalQuestion,
        shortAnswer,
        sessionId,
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Update the message that was explained
      setMessages((prev) => {
        const updated = [...prev];
        // Find the last assistant message and mark it as explained
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].role === 'assistant' && updated[i].canExplainMore) {
            updated[i] = { ...updated[i], canExplainMore: false };
            break;
          }
        }
        // Add the detailed explanation as a new message
        return [
          ...updated,
          {
            id: crypto.randomUUID(),
            role: 'assistant' as const,
            content: data.message,
            citations: data.citations,
            timestamp: new Date(),
            error: data.error,
            queryId: data.queryId,
            isExplanation: true,
          },
        ];
      });
    },
    onError: () => {
      toast.error('Failed to get detailed explanation');
    },
  });

  const handleExplainMore = (originalQuestion: string, shortAnswer: string) => {
    explainMoreMutation.mutate({ originalQuestion, shortAnswer });
  };

  const feedbackMutation = useMutation({
    mutationFn: async ({ queryId, rating }: { queryId: string; rating: FeedbackRating }) => {
      const response = await apiClient.post('/feedback', { queryId, rating });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Update the message to show feedback was given
      setMessages((prev) =>
        prev.map((msg) =>
          msg.queryId === variables.queryId
            ? { ...msg, feedbackGiven: variables.rating }
            : msg
        )
      );
      toast.success('Thanks for your feedback!');
    },
    onError: () => {
      toast.error('Failed to submit feedback');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || chatMutation.isPending) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    chatMutation.mutate(input.trim());
    setInput('');
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(undefined);
  };

  const handleFeedback = (queryId: string, rating: FeedbackRating) => {
    feedbackMutation.mutate({ queryId, rating });
  };

  const FeedbackButtons = ({ message }: { message: ChatMessage }) => {
    if (!message.queryId || message.error) return null;

    const buttonClass = "h-7 w-7 p-0";
    const activeClass = "bg-primary/10";

    return (
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border/30">
        <span className="text-xs text-muted-foreground mr-2">Was this helpful?</span>
        <Button
          variant="ghost"
          size="sm"
          className={cn(buttonClass, message.feedbackGiven === 'helpful' && activeClass)}
          onClick={() => handleFeedback(message.queryId!, 'helpful')}
          disabled={!!message.feedbackGiven || feedbackMutation.isPending}
          title="Helpful"
        >
          <ThumbsUp className={cn("h-3.5 w-3.5", message.feedbackGiven === 'helpful' && "text-green-500")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(buttonClass, message.feedbackGiven === 'not_helpful' && activeClass)}
          onClick={() => handleFeedback(message.queryId!, 'not_helpful')}
          disabled={!!message.feedbackGiven || feedbackMutation.isPending}
          title="Not helpful"
        >
          <ThumbsDown className={cn("h-3.5 w-3.5", message.feedbackGiven === 'not_helpful' && "text-red-500")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(buttonClass, message.feedbackGiven === 'incorrect' && activeClass)}
          onClick={() => handleFeedback(message.queryId!, 'incorrect')}
          disabled={!!message.feedbackGiven || feedbackMutation.isPending}
          title="Incorrect information"
        >
          <MessageSquareWarning className={cn("h-3.5 w-3.5", message.feedbackGiven === 'incorrect' && "text-orange-500")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(buttonClass, message.feedbackGiven === 'incomplete' && activeClass)}
          onClick={() => handleFeedback(message.queryId!, 'incomplete')}
          disabled={!!message.feedbackGiven || feedbackMutation.isPending}
          title="Incomplete answer"
        >
          <FileQuestion className={cn("h-3.5 w-3.5", message.feedbackGiven === 'incomplete' && "text-yellow-500")} />
        </Button>
        {message.feedbackGiven && (
          <span className="text-xs text-muted-foreground ml-2">
            Feedback recorded
          </span>
        )}
      </div>
    );
  };

  const ExplainMoreButton = ({ message }: { message: ChatMessage }) => {
    if (!message.canExplainMore || message.error || !message.originalQuestion) return null;

    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs text-primary hover:text-primary/80 px-2 mt-2"
        onClick={() => handleExplainMore(message.originalQuestion!, message.content)}
        disabled={explainMoreMutation.isPending}
      >
        {explainMoreMutation.isPending ? (
          <>
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Loading...
          </>
        ) : (
          <>
            <ChevronDown className="h-3 w-3 mr-1" />
            Explain more
          </>
        )}
      </Button>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <BookOpen className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Chat inside Knowledge Base</h1>
            <p className="text-sm text-muted-foreground">
              Chat with your documents to test the RAG system
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <Button variant="outline" size="sm" onClick={clearChat}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Chat
          </Button>
        )}
      </div>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        <CardHeader className="py-3 px-4 border-b">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Bot className="h-4 w-4" />
            Knowledge Base Assistant
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 p-0 overflow-hidden">
          <ScrollArea ref={scrollAreaRef} className="h-full">
            <div className="p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[50vh] text-center">
                  <BookOpen className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-medium mb-2">Start a Conversation</h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    Ask questions about your uploaded documents. The AI will search through
                    your knowledge base and provide answers with citations.
                  </p>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-lg">
                    {[
                      'What are the key topics in my documents?',
                      'Summarize the main findings',
                      'What policies are mentioned?',
                      'Find information about...',
                    ].map((suggestion) => (
                      <Button
                        key={suggestion}
                        variant="outline"
                        size="sm"
                        className="text-left justify-start h-auto py-2 px-3"
                        onClick={() => {
                          setInput(suggestion);
                          inputRef.current?.focus();
                        }}
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      'flex gap-3',
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    )}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Bot className="h-4 w-4" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'max-w-[80%] rounded-lg px-4 py-3',
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : message.error
                          ? 'bg-destructive/10 border border-destructive/20'
                          : 'bg-muted'
                      )}
                    >
                      {message.error && (
                        <div className="flex items-center gap-2 text-destructive mb-2">
                          <AlertCircle className="h-4 w-4" />
                          <span className="text-xs font-medium">Error</span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                      {/* Citations */}
                      {message.citations && message.citations.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border/50">
                          <p className="text-xs font-medium mb-2 opacity-70">Sources:</p>
                          <div className="space-y-2">
                            {message.citations.map((citation) => (
                              <div
                                key={citation.id}
                                className="flex items-start gap-2 text-xs bg-background/50 rounded p-2"
                              >
                                <FileText className="h-3 w-3 mt-0.5 shrink-0" />
                                <div>
                                  <span className="font-medium">{citation.filename}</span>
                                  <p className="text-muted-foreground mt-0.5 line-clamp-2">
                                    {citation.excerpt}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Explain More button */}
                      {message.role === 'assistant' && <ExplainMoreButton message={message} />}

                      {/* Feedback buttons for assistant messages */}
                      {message.role === 'assistant' && <FeedbackButtons message={message} />}
                    </div>
                    {message.role === 'user' && (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                        <User className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                ))
              )}

              {/* Loading indicator */}
              {chatMutation.isPending && (
                <div className="flex gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="bg-muted rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">
                        Searching knowledge base...
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>

        {/* Input Area */}
        <div className="border-t p-4">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question about your documents..."
              disabled={chatMutation.isPending}
              className="flex-1"
            />
            <Button type="submit" disabled={!input.trim() || chatMutation.isPending}>
              {chatMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Responses are generated from your uploaded documents using RAG (Retrieval-Augmented Generation)
          </p>
        </div>
      </Card>
    </div>
  );
}

export default KBChatPage;
